// useAbortableEffect — useEffect wrapper that provides a per-run
// AbortSignal, aborting the previous controller when dependencies
// change or the component unmounts.
//
// Motivation (issue #42, build spec §10a N13):
//   Debounced inputs and any fetch whose deps change faster than
//   the request resolves are vulnerable to response races — older
//   responses landing after newer ones and overwriting state. Pair
//   this hook with `apiFetch(path, { signal })` to cancel the
//   superseded request at the network layer.
//
// Scope note: AbortController is for legitimate same-scope
// supersession (debounced search, changing filters within one
// page). URL-drill races across scopes are already handled by
// scope-matched state (§5 convention) — don't use this hook to
// redundantly guard those.
//
// Usage:
//   useAbortableEffect((signal) => {
//     apiFetch('/search', { signal })
//       .then(setResults)
//       .catch((err) => {
//         if (isAbortError(err)) return
//         setError(err)
//       })
//   }, [query])
//
// The effect callback may optionally return a cleanup function,
// matching React's `useEffect` contract. The hook always aborts
// the controller on dep-change / unmount, regardless of whether
// the callback returned its own cleanup.

import { useEffect, useRef, useState } from 'react'

// Callback may optionally return a cleanup function (matching
// React's useEffect contract). `undefined` is used in the union
// instead of `void` so TypeScript's no-invalid-void-type rule is
// happy while still letting callers `return` nothing.
export type AbortableEffectCleanup = () => void
export type AbortableEffectCallback = (
  signal: AbortSignal,
) => AbortableEffectCleanup | undefined

function depsEqual(
  a: React.DependencyList,
  b: React.DependencyList,
): boolean {
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) {
    if (!Object.is(a[i], b[i])) return false
  }
  return true
}

interface DepState {
  token: number
  deps: React.DependencyList
}

export function useAbortableEffect(
  effect: AbortableEffectCallback,
  deps: React.DependencyList,
): void {
  // Collapse the caller's (variable-length) dep list into a single
  // scalar `token` that only changes on shallow dep change. The
  // inner `useEffect` then depends on `[token]` — a literal,
  // fixed-arity array — so `react-hooks/exhaustive-deps` has nothing
  // to complain about and we avoid both the "not an array literal"
  // warning (spread elements) and the "missing dep" warning on
  // `effect`.
  //
  // The token lives in state (not a ref) so we never read or write
  // a ref during render — that would trip React 19's `react-hooks/
  // refs` rule. Derived-state-from-props updates during render are
  // React's documented pattern for this shape of problem
  // (react.dev "You Might Not Need an Effect" §"Adjusting some
  // state when a prop changes").
  const [state, setState] = useState<DepState>(() => ({
    token: 0,
    deps,
  }))
  if (!depsEqual(deps, state.deps)) {
    setState({ token: state.token + 1, deps })
  }

  // Latest-callback ref updated in a post-commit effect so callers
  // can pass inline arrow functions without re-triggering the main
  // effect on every render. Writing to `.current` inside useEffect
  // (not during render) is allowed by the `react-hooks/refs` rule.
  const effectRef = useRef(effect)
  useEffect(() => {
    effectRef.current = effect
  })

  useEffect(() => {
    const controller = new AbortController()
    const cleanup = effectRef.current(controller.signal)
    return () => {
      if (typeof cleanup === 'function') cleanup()
      controller.abort()
    }
  }, [state.token])
}
